/**
 * Locked frosting structure totals per 0.5 kg (Structure 1 = 500 g, Structure 2 = 515 g).
 * RTG = standard outside (Classic / RTG). Custom = customer-chosen outside.
 */

const STRUCTURE_1_REGULAR = {
  total: 500,
  rows: [
    { code: "B", label: "Base", a: "255" },
    { code: "S", label: "Soaking", a: "20" },
    { code: "WC", label: "Whipped cream", a: "100" },
    { code: "F", label: "Filling", a: "60" },
    { code: "T", label: "Toppings", a: "20" },
    { code: "DW", label: "Deco whipped cream", a: "25" },
    { code: "DF", label: "Deco filling cream", a: "20" },
  ],
};

const STRUCTURE_1_CUSTOM = {
  total: 500,
  rows: [
    { code: "B", label: "Base", b: "255" },
    { code: "S", label: "Soaking", b: "20" },
    { code: "WC", label: "Whipped cream", b: "125" },
    { code: "F", label: "Filling", b: "80", bFruitsSweet: "50" },
    { code: "T", label: "Toppings", b: "20", bFruitsSweet: "50" },
  ],
};

const STRUCTURE_2_REGULAR = {
  total: 515,
  rows: [
    { code: "B", label: "Base", a: "255" },
    { code: "S", label: "Soaking", a: "20" },
    { code: "CC", label: "Choco/cheese cream", a: "190" },
    { code: "T", label: "Toppings", a: "10" },
    { code: "DCC", label: "Deco choco/cheese", a: "20" },
    { code: "DT", label: "Deco toppings", a: "20" },
  ],
};

const STRUCTURE_2_CUSTOM = {
  total: 515,
  rows: [
    { code: "B", label: "Base", b: "255" },
    { code: "S", label: "Soaking", b: "20" },
    { code: "CC", label: "Choco/cheese cream", b: "190" },
    { code: "T", label: "Toppings", b: "10" },
    { code: "WC", label: "Whipped cream", b: "40" },
  ],
};

const FRUITS_SWEET_KEYS = new Set([
  "mango",
  "mix fruit",
  "mixfruit",
  "rasmalai",
  "ras malai",
]);

const STRUCTURE_2_FLAVORS = new Set([
  "chocolate truffle",
  "belgian chocolate",
  "milk chocolate",
  "red velvet",
  "sugar free dark chocolate",
]);

function normalizeFlavorKey(flavor) {
  return (flavor || "")
    .trim()
    .toLowerCase()
    .replace(/\s+cake$/i, "")
    .replace(/\s+/g, " ");
}

export function isFruitsSweetFlavor(flavor) {
  const k = normalizeFlavorKey(flavor);
  return FRUITS_SWEET_KEYS.has(k) || k.includes("mix fruit") || k.includes("mango") || k.includes("rasmalai");
}

function isStructure2(flavor) {
  const k = normalizeFlavorKey(flavor);
  return STRUCTURE_2_FLAVORS.has(k) || k.includes("chocolate truffle") || k.includes("belgian") || k.includes("red velvet") || k.includes("milk chocolate");
}

function mergeStructure(regular, custom, fruitsSweet) {
  const codes = new Set([
    ...regular.rows.map((r) => r.code),
    ...custom.rows.map((r) => r.code),
  ]);
  const labelByCode = {};
  regular.rows.forEach((r) => {
    labelByCode[r.code] = r.label;
  });
  custom.rows.forEach((r) => {
    labelByCode[r.code] = r.label;
  });

  const aByCode = Object.fromEntries(regular.rows.map((r) => [r.code, r.a]));
  const bByCode = Object.fromEntries(
    custom.rows.map((r) => {
      if (fruitsSweet && r.bFruitsSweet != null) return [r.code, r.bFruitsSweet];
      return [r.code, r.b];
    })
  );

  const rows = [...codes].map((code) => ({
    code,
    label: labelByCode[code] || code,
    a: aByCode[code] ?? "—",
    b: bByCode[code] ?? "—",
  }));

  return {
    structureFamily: regular.total === 515 ? 2 : 1,
    totalGrams: regular.total,
    fruitsSweetNote: fruitsSweet,
    rows,
  };
}

/** Build display rows from API document (recipes) or defaults. */
export function structureDocumentToDisplay(doc, flavor) {
  if (doc?.rows?.length) {
    const totalGrams = doc.total_half_kg || (doc.structure_family === 2 ? 515 : 500);
    const rows = doc.rows.map((row) => ({
      code: row.code,
      label: row.label || row.code,
      a: row.rtg != null && row.rtg !== "" ? String(row.rtg) : "—",
      b: row.custom != null && row.custom !== "" ? String(row.custom) : "—",
    }));
    return {
      structureFamily: doc.structure_family === 2 ? 2 : 1,
      totalGrams,
      fruitsSweetNote: Boolean(doc.fruits_sweet_custom),
      rows,
      fromRecipe: Boolean(doc.from_recipe),
    };
  }
  return getFrostingStructureForFlavor(flavor);
}

export function getFrostingStructureForFlavor(flavor) {
  const fruitsSweet = isFruitsSweetFlavor(flavor);
  if (isStructure2(flavor)) {
    return mergeStructure(STRUCTURE_2_REGULAR, STRUCTURE_2_CUSTOM, false);
  }
  return mergeStructure(STRUCTURE_1_REGULAR, STRUCTURE_1_CUSTOM, fruitsSweet);
}

export function getFrostingStructureTitleFromDoc(doc, flavor) {
  const s = doc?.rows?.length ? structureDocumentToDisplay(doc, flavor) : getFrostingStructureForFlavor(flavor);
  const family = s.structureFamily === 2 ? "Structure 2 (515 g)" : "Structure 1 (500 g)";
  const note = s.fruitsSweetNote ? " · Fruits & Sweet (Custom)" : "";
  const saved = doc?.from_recipe ? " · Saved" : "";
  return `${family}${note}${saved}`;
}

/** Scale RTG or Custom template dict (0.5 kg grams) to cake weight. */
export function scaleTemplateDict(template, weightKg) {
  const mult = Math.max(Number(weightKg) || 0.5, 0.25) / 0.5;
  const components = {};
  let total = 0;
  for (const [code, raw] of Object.entries(template || {})) {
    const n = Number(raw);
    if (!Number.isFinite(n)) continue;
    const scaled = Math.round(n * mult * 100) / 100;
    components[code] = scaled;
    total += scaled;
  }
  return { components, totalGrams: Math.round(total * 100) / 100 };
}

/** Prefer block.templates from API (recipe DB); else hardcoded flavor defaults. */
export function getScaledFrostingStructureFromBlock(block, weightKg) {
  if (!block?.kind) return null;
  const tpl =
    block.kind === "rtg" ? block.templates?.rtg : block.templates?.custom;
  if (tpl && Object.keys(tpl).length > 0) {
    const scaled = scaleTemplateDict(tpl, weightKg);
    return {
      kind: block.kind,
      kindLabel: block.kind === "rtg" ? "RTG" : "Custom",
      structureFamily: block.structure_family,
      weightKg: Number(weightKg) || 0.5,
      components: scaled.components,
      totalGrams: scaled.totalGrams,
      fruitsSweetNote: block.fruits_sweet,
    };
  }
  return getScaledFrostingStructure(
    block.flavor || block.label?.split(" (")[0] || "",
    weightKg,
    block.kind
  );
}

/** @param {"RTG"|"Custom"|"rtg"|"custom"} orderType */
export function resolveFrostingKindFromOrderType(orderType, isRtgFlag = false) {
  const ot = String(orderType || "Custom").trim();
  if (isRtgFlag || ot === "RTG") return "rtg";
  if (ot === "Custom") return "custom";
  return null;
}

/** Scaled structure for one cake line (matches backend cake_frosting_structures.py). */
export function getScaledFrostingStructure(flavor, weightKg, kind) {
  if (!kind) return null;
  const base = getFrostingStructureForFlavor(flavor);
  const mult = Math.max(Number(weightKg) || 0.5, 0.25) / 0.5;
  const pick = kind === "rtg" ? "a" : "b";
  const components = {};
  let total = 0;
  for (const row of base.rows) {
    const raw = row[pick];
    if (raw === "—" || raw == null) continue;
    const n = Number(raw);
    if (!Number.isFinite(n)) continue;
    const scaled = Math.round(n * mult * 100) / 100;
    components[row.code] = scaled;
    total += scaled;
  }
  return {
    kind,
    kindLabel: kind === "rtg" ? "RTG" : "Custom",
    structureFamily: base.structureFamily,
    weightKg: Number(weightKg) || 0.5,
    components,
    totalGrams: Math.round(total * 100) / 100,
    fruitsSweetNote: kind === "custom" && base.fruitsSweetNote,
  };
}

export function getFrostingStructureTitle(flavor, doc = null) {
  if (doc) return getFrostingStructureTitleFromDoc(doc, flavor);
  const s = getFrostingStructureForFlavor(flavor);
  const family = s.structureFamily === 2 ? "Structure 2 (515 g)" : "Structure 1 (500 g)";
  const note = s.fruitsSweetNote ? " · Fruits & Sweet (Custom)" : "";
  return `${family}${note}`;
}

/** API body for PUT /recipes/:id/frosting-structure (persists all entered grams). */
export function rowsToFrostingPayload(rows, existingDoc, flavor) {
  const family =
    existingDoc?.structure_family != null
      ? Number(existingDoc.structure_family)
      : isStructure2(flavor)
        ? 2
        : 1;
  const totalHalfKg =
    existingDoc?.total_half_kg != null
      ? Number(existingDoc.total_half_kg)
      : family === 2
        ? 515
        : 500;
  const fruitsSweet =
    existingDoc?.fruits_sweet_custom != null
      ? Boolean(existingDoc.fruits_sweet_custom)
      : isFruitsSweetFlavor(flavor);

  return {
    structure_family: family,
    total_half_kg: totalHalfKg,
    fruits_sweet_custom: fruitsSweet,
    rows: (rows || []).map((row) => {
      const rtgRaw = row.rtg;
      const customRaw = row.custom;
      return {
        code: String(row.code || "").trim().toUpperCase(),
        label: row.label || row.code,
        rtg:
          rtgRaw === "" || rtgRaw == null || Number.isNaN(Number(rtgRaw))
            ? null
            : Number(rtgRaw),
        custom:
          customRaw === "" || customRaw == null || Number.isNaN(Number(customRaw))
            ? null
            : Number(customRaw),
      };
    }),
  };
}

/** RTG deco — full scaled amount once (not split by L). */
const RTG_FULL_AMOUNT_CODES = new Set(["DW", "DF", "DCC", "DT"]);

/**
 * Per-layer divisors. Custom Structure 2 (chocolate / red velvet): CC ÷ L, WC once.
 * RTG Structure 2: CC ÷ (L+1). Structure 1 unchanged for both kinds.
 */
function layerDivisor(code, structureFamily, kind, layers) {
  const L = Math.max(1, Math.floor(Number(layers) || 1));
  const rtg = kind === "rtg";
  const custom = kind === "custom";
  const family2 = Number(structureFamily) === 2;

  if (rtg && RTG_FULL_AMOUNT_CODES.has(code)) return 1;

  if (family2) {
    if (code === "B" || code === "S") return L;
    if (code === "T") return L - 1;
    if (code === "CC") return custom ? L : L + 1;
    if (code === "WC") return custom ? 1 : 0;
    return L;
  }

  if (code === "B" || code === "S") return L;
  if (code === "WC") return L + 2;
  if (code === "F" || code === "T") return L - 1;
  return L;
}

/**
 * Per-use grams for each code given base layers L (froster input).
 * @returns {Object<string, number|null>} null when divisor invalid (e.g. F/T with L=1)
 */
export function computePerLayerComponents(scaled, structureFamily, kind, layers) {
  const out = {};
  for (const [code, grams] of Object.entries(scaled || {})) {
    const g = Number(grams);
    if (!Number.isFinite(g)) continue;
    const div = layerDivisor(code, structureFamily, kind, layers);
    if (div <= 0) {
      out[code] = null;
      continue;
    }
    out[code] = Math.round((g / div) * 100) / 100;
  }
  return out;
}

export function formatUsesHint(code, layers, structureFamily, kind) {
  const div = layerDivisor(code, structureFamily, kind, layers);
  if (div <= 0) return "—";
  if (div === 1) return "1×";
  return `${div}×`;
}

export function sumComponents(components) {
  return Object.values(components || {}).reduce(
    (acc, v) => acc + (Number.isFinite(Number(v)) ? Number(v) : 0),
    0
  );
}

export function formatFormulaLine(components) {
  return Object.entries(components || {})
    .filter(([, v]) => v != null && Number.isFinite(Number(v)))
    .map(([code, g]) => `${code} ${g}`)
    .join(" + ");
}

/** Suggested per-base weights (kg) for tier cakes; bottom tier first. */
export function suggestTierBaseWeights(totalKg, tierBaseCount) {
  const total = Math.max(Number(totalKg) || 0.5, 0.25);
  const n = Math.max(2, Math.floor(Number(tierBaseCount) || 2));
  if (n === 2) {
    const top = Math.round((total / 3) * 100) / 100;
    const bottom = Math.round((total - top) * 100) / 100;
    return [bottom, top];
  }
  const each = Math.round((total / n) * 100) / 100;
  const weights = Array(n).fill(each);
  const sum = weights.reduce((a, b) => a + b, 0);
  weights[n - 1] = Math.round((weights[n - 1] + (total - sum)) * 100) / 100;
  return weights;
}
